import { Group } from 'src/design/atoms/Group';
import { IconDownload } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useCallback, useMemo } from 'react';
import { workflowStatusColumn } from 'src/app/workflows/workflowStatusColumn';
import {
  changeAmountColumns,
  changeAmountExportRow,
} from 'src/app/changes/changeAmountColumns';
import { useNavigate } from 'react-router-dom';
import { ChangeDto } from 'src/api/FiretrolApiClient';
import { useJobs } from 'src/hooks/useJobs';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { ViewActionIcon } from 'src/design/atoms/ViewActionIcon';
import { usePreviewChangePdf } from 'src/api/queries/changes/useChangeMutations';
import { useWorkflowPdfDownload } from 'src/app/workflows/useWorkflowPdfDownload';
import { useTableExports } from 'src/design/organisms/useTableExports';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';

interface IChangesTableProps {
  changes: ChangeDto[];
  loading?: boolean;
  openInNewTab?: boolean;
}

export const ChangesTable: React.FC<IChangesTableProps> = ({
  changes,
  loading = true,
  openInNewTab = false,
}) => {
  const navigate = useNavigate();
  const { selectedJob } = useJobs();
  const previewChangePdf = usePreviewChangePdf();
  const { download, downloadingId } = useWorkflowPdfDownload({
    fetchPdf: (jobId, id) => previewChangePdf.mutateAsync({ jobId, id }),
    logLabel: 'Failed to download change order PDF',
  });

  const handleEditChange = useCallback(
    (row: ChangeDto) => {
      if (openInNewTab) {
        window.open(`/jobs/${row.jobId}/changes/view/${row.id}`, '_blank');
      } else {
        navigate(`/jobs/${row.jobId}/changes/view/${row.id}`);
      }
    },
    [navigate, openInNewTab],
  );

  const handleDownloadPdf = (row: ChangeDto) => {
    if (!row.jobId) return;
    download({
      jobId: row.jobId,
      id: row.id,
      fileName: `${row.changeNumber ?? 'change-order'}.pdf`,
    });
  };

  const columns = useMemo<MRT_ColumnDef<ChangeDto>[]>(
    () => [
      {
        accessorKey: 'changeNumber',
        header: 'Change',
        size: 150,
      },
      workflowStatusColumn<ChangeDto>({ size: 150 }),
      ...changeAmountColumns<ChangeDto>(),
      {
        id: 'createdBy',
        accessorFn: (row) => row.createdBy?.fullName,
        header: 'Created By',
        size: 150,
      },
      {
        id: 'assignee',
        accessorFn: (row) => row.assignee?.fullName,
        header: 'Assignee',
        size: 150,
      },
    ],
    [],
  );

  const tableState = useTableState({
    identifier: 'changes-table',
    columns: columns,
    defaultSorting: [{ id: 'changeNumber', desc: false }],
  });

  const toRow = (item: ChangeDto) => ({
    ['Job Name']: selectedJob?.name,
    ['Job Number']: selectedJob?.jobNumber,
    ['Change']: item.changeNumber,
    ['Status']: item.status,
    ...changeAmountExportRow(item),
    ['Created By']: item.createdBy?.fullName || '',
    ['Assignee']: item.assignee?.fullName || '',
  });

  const tableExports = useTableExports<ChangeDto>('change-orders', (data) =>
    data.map(toRow),
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 120 } },
      renderRowActions: ({ row }) => (
        <Group>
          <ViewActionIcon
            onClick={() => handleEditChange(row.original)}
            label="View Change"
          />
          <BasicActionIcon
            onClick={() => handleDownloadPdf(row.original)}
            label="Download PDF"
            loading={downloadingId === row.original.id}
          >
            <IconDownload size={20} />
          </BasicActionIcon>
        </Group>
      ),
      enableClickToCopy: false,
      columns: columns,
      data: changes,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
    },
    getRowId: (row) => String(row.id),
    totalCount: changes.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  return table;
};
